/**
 * Has a newer build been deployed since this tab loaded?
 *
 * A deploy replaces every chunk hash, so a tab still running the old build
 * fails its next lazy import: the founder saw a brief "Something went wrong",
 * then the self-heal reload (lib/chunkReload), then a working page (2026-10-05).
 * Knowing about the new build BEFORE the click lets the router turn that click
 * into an ordinary full page load of the destination — no failed import, no
 * error flash.
 *
 * The running build is VITE_APP_VERSION (git short sha, vite.config.ts); the
 * deployed one is soltik-web's /__version (full sha of the same commit).
 */

const BUILD = String((import.meta as any).env?.VITE_APP_VERSION || '');
/** Look again at most this often (on focus / tab return / the timer). */
const MIN_GAP_MS = 60_000;
const POLL_MS = 3 * 60_000;

let stale = false;
let lastCheck = 0;
let started = false;

/** True once the server reports a different build than this tab runs. */
export function isStaleBuild(): boolean {
  return stale;
}

/** Pure: does the deployed sha differ from the running short sha? */
export function differsFrom(running: string, deployed: string | null | undefined): boolean {
  if (!/^[0-9a-f]{7,40}$/i.test(running) || !deployed || !/^[0-9a-f]{7,40}$/i.test(deployed)) return false;
  return !deployed.toLowerCase().startsWith(running.toLowerCase());
}

export async function checkForNewBuild(force = false): Promise<boolean> {
  if (stale) return true;
  const now = Date.now();
  if (!force && now - lastCheck < MIN_GAP_MS) return stale;
  lastCheck = now;
  try {
    const res = await fetch('/__version', { cache: 'no-store' });
    if (!res.ok) return stale;
    const j = await res.json().catch(() => null) as { sha?: string } | null;
    if (differsFrom(BUILD, j?.sha)) stale = true;
  } catch { /* offline: keep what we know */ }
  return stale;
}

/** Start watching: a slow timer, plus whenever the tab comes back to the front. */
export function watchForNewBuild(): void {
  if (started || typeof window === 'undefined' || !/^[0-9a-f]{7,40}$/i.test(BUILD)) return;
  started = true;
  const check = () => { if (document.visibilityState === 'visible') void checkForNewBuild(); };
  window.setInterval(check, POLL_MS);
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  void checkForNewBuild(true);
}
